import React from 'react';
import './Onboarding.css';
import GuidedTourOverlay from './guidedTour/GuidedTourOverlay';
import type { GuidedTourStep } from './guidedTour/types';
import { useGuidedTour } from '../hooks/useGuidedTour';

interface MultiAgentOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS: GuidedTourStep[] = [
  {
    id: 'hero-title',
    title: 'Добро пожаловать в мультиагентный AI Tuner',
    description:
      'Configure and optimize multiple AI agents working together — coordinator plus specialized workers.',
    position: 'right',
    offset: { x: 60, y: 0 },
  },
  {
    id: 'configure-section',
    title: 'Мультиагентная конфигурация',
    description:
      'Настройте координатора и агентов-исполнителей: модели, температуры, промпты и роли.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'evaluation-dataset',
    title: 'Датасет оценок',
    description: 'Выберите датасет, по которому оценивается работа агентов во время оптимизации.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'iterations-setting',
    title: 'Итерации оптимизации',
    description: 'Больше итераций может улучшить результат, но увеличит время и расход токенов.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'worker-count',
    title: 'Число агентов-исполнителей',
    description: 'Задайте, сколько агентов-исполнителей участвует в запуске.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'coordinator-section',
    title: 'Агент-координатор',
    description: 'Координатор управляет исполнителями и формирует итоговый ответ пользователю.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'workers-section',
    title: 'Агенты-исполнители',
    description: 'У каждого исполнителя свой промпт, модель и специализация.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'optimize-button',
    title: 'Запуск оптимизации',
    description: 'Запустите оптимизацию, когда конфигурация и датасет готовы.',
    position: 'top',
    offset: { x: 0, y: -30 },
    scrollIntoViewBlock: 'nearest',
  },
  {
    id: 'visualization-section',
    title: 'Визуализация конфигурации',
    description: 'Схема связей агентов и параметров в компактном виде.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'evaluation-graphs',
    title: 'Метрики оценки',
    description: 'Следите за оценками и движением эмбеддингов во время и после оптимизации.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'chat-comparison',
    title: 'Сравнение мультиагентных чатов',
    description: 'Сравнивайте ответы ассистента по итерациям в чатах рядом.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
];

const MultiAgentOnboarding = ({ isOpen, onClose }: MultiAgentOnboardingProps) => {
  const tour = useGuidedTour({
    isOpen,
    steps: STEPS,
    onClose,
    layout: 'center',
  });

  if (!isOpen || !tour.currentStepData) return null;

  return (
    <GuidedTourOverlay
      isOpen={isOpen}
      tourTitle="Экскурсия по мультиагентному режиму"
      stepsCount={tour.stepCount}
      currentStep={tour.safeStepIndex}
      stepTitle={tour.currentStepData.title}
      stepDescription={tour.currentStepData.description}
      tooltipX={tour.tooltipPosition.x}
      tooltipY={tour.tooltipPosition.y}
      position={tour.currentPosition}
      onClose={tour.handleSkip}
      onNext={tour.handleNext}
      onPrev={tour.handlePrev}
    />
  );
};

export default MultiAgentOnboarding;
